
import React, { createContext, useContext, useState, useEffect } from 'react';
import { Globe } from 'lucide-react';
import { Language } from '../types';
import { useAuth } from './AuthContext';
import { updateUserLanguage } from '../services/authService';
import { FullPageLoader } from '../components/Common/FullPageLoader';
import { t } from '../i18n';

interface LanguageContextType {
  lang: Language;
  setLang: (l: Language) => void;
  isSyncing: boolean;
}

export const LanguageContext = createContext<LanguageContextType>({ 
  lang: 'en', 
  setLang: () => {},
  isSyncing: false
});

export const useLanguage = () => useContext(LanguageContext);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, updateUser } = useAuth();
  const [isSyncing, setIsSyncing] = useState(false);
  
  // Detection logic: Storage -> Browser Locale -> Default 'en'
  const detectLang = (): Language => {
    const savedLang = localStorage.getItem('app_lang') as Language;
    if (savedLang) return savedLang;
    
    const browserLang = navigator.language || (navigator as any).userLanguage;
    if (browserLang?.startsWith('ko')) return 'ko';
    if (browserLang?.startsWith('ja')) return 'ja';
    if (browserLang?.startsWith('es')) return 'es';
    if (browserLang?.startsWith('fr')) return 'fr';
    return 'en';
  };

  const [lang, setLangState] = useState<Language>(detectLang());

  // Handle language sync when auth state changes
  useEffect(() => {
    if (user) {
      // If user is logged in, prioritize their account preference
      if (user.lang && user.lang !== lang) {
        setLangState(user.lang);
        localStorage.setItem('app_lang', user.lang);
      }
    } else {
      // If user is logged out (or just logged out), re-detect based on storage/browser
      // This ensures that when AuthContext clears 'app_lang', the UI resets immediately
      const current = detectLang();
      if (current !== lang) {
        setLangState(current);
      }
    }
  }, [user?.id, user === null]); // React to login and explicit logout

  const handleSetLang = async (l: Language) => {
    if (l === lang) return;

    setIsSyncing(true);
    
    // Immediate local update
    setLangState(l);
    localStorage.setItem('app_lang', l);
    
    // DB sync if logged in
    if (user) {
        try {
            await updateUserLanguage(user.id, l);
            updateUser({ lang: l });
        } catch (e) {
            console.error("Failed to sync language to DB", e);
        }
    }

    // Clear sync overlay after 3s
    setTimeout(() => {
        setIsSyncing(false);
    }, 3000);
  };

  return (
    <LanguageContext.Provider value={{ lang, setLang: handleSetLang, isSyncing }}>
      {isSyncing && (
        <div className="fixed inset-0 z-[200]">
          <FullPageLoader 
            isLoading={true} 
            icon={Globe} 
            text={t(lang, 'changingLanguage')} 
          />
        </div>
      )}
      {children}
    </LanguageContext.Provider>
  );
};
